import { useCallback, useEffect } from 'react'

import { invokeAndroidMethod } from './androidBridge'
import { PrinterState, type BlockingPrinterState } from './printerState'

export interface StartPaymentRequest {
  orderId: string
}

export interface CancelPaymentRequest {
  orderId: string
}

export interface PrintOrderRequest {
  orderId: string
}

export interface PrintOrderResult {
  message?: string
  [key: string]: unknown
}

export interface PrinterStatus {
  connected: boolean
  ready: boolean
  paperEmpty: boolean
  paperNearEmpty: boolean
  coverOpen: boolean
  hasError: boolean
}

interface UseTerminalOptions {
  onPaymentStatusUpdate?: (status: unknown) => void
}

declare global {
  interface Window {
    onPaymentStatusUpdate?: ((statusPayload: unknown) => void) | undefined
  }
}

const parseStatusPayload = (statusPayload: unknown) => {
  if (typeof statusPayload !== 'string') {
    return statusPayload
  }

  try {
    return JSON.parse(statusPayload) as unknown
  } catch {
    return statusPayload
  }
}

const parsePrinterStatus = (value: unknown): PrinterStatus => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Android.getPrinterStatus returned invalid payload.')
  }

  const status = value as Record<string, unknown>
  const connected = status.connected
  const ready = status.ready
  const paperEmpty = status.paperEmpty
  const paperNearEmpty = status.paperNearEmpty
  const coverOpen = status.coverOpen
  const hasError = status.hasError

  if (
    typeof connected !== 'boolean' ||
    typeof ready !== 'boolean' ||
    typeof paperEmpty !== 'boolean' ||
    typeof paperNearEmpty !== 'boolean' ||
    typeof coverOpen !== 'boolean' ||
    typeof hasError !== 'boolean'
  ) {
    throw new Error('Android.getPrinterStatus returned invalid payload.')
  }

  return { connected, ready, paperEmpty, paperNearEmpty, coverOpen, hasError }
}

// Mirrors the Intro screen's printer-status mapping (Intro.tsx), but stricter on the
// "sellable" branch by design: require the printer to be affirmatively healthy so the
// charge gate never relies implicitly on the native invariant that ready===true also
// implies !hasError/!paperEmpty/!coverOpen. PrinterState lives in ./printerState.
export const derivePrinterState = (status: PrinterStatus): PrinterState => {
  if (status.ready && !status.hasError && !status.paperEmpty && !status.coverOpen) {
    return status.paperNearEmpty ? PrinterState.PAPER_NEAR_EMPTY : PrinterState.READY
  }
  if (!status.connected) return PrinterState.OFFLINE
  if (status.coverOpen) return PrinterState.COVER_OPEN
  if (status.paperEmpty) return PrinterState.NO_PAPER
  return PrinterState.UNKNOWN_ERROR
}

// Thrown by startPayment when the receipt printer is not ready to print. Carries the
// concrete state so the UI can render the matching Out-of-Order message.
export class PrinterNotReadyError extends Error {
  readonly printerState: BlockingPrinterState

  constructor(printerState: BlockingPrinterState, detail?: string) {
    super(`Printer not ready for payment: ${printerState}${detail ? ` (${detail})` : ''}`)
    this.name = 'PrinterNotReadyError'
    this.printerState = printerState
  }
}

// Fresh printer check taken immediately before the charge. Fails CLOSED: if the status
// cannot even be read (bridge timeout while the Star controller is hung, invalid
// payload), we refuse the charge rather than risk a charged-without-receipt sale.
const assertPrinterReady = async () => {
  let status: PrinterStatus
  try {
    const result = await invokeAndroidMethod<unknown>('getPrinterStatus')
    status = parsePrinterStatus(result)
  } catch (error) {
    throw new PrinterNotReadyError(PrinterState.UNKNOWN_ERROR, error instanceof Error ? error.message : String(error))
  }

  const state = derivePrinterState(status)
  if (state === PrinterState.READY || state === PrinterState.PAPER_NEAR_EMPTY) {
    return
  }

  throw new PrinterNotReadyError(state)
}

// Refuse concurrent startPayment for the same orderId — second call returns the same
// in-flight Promise instead of opening a parallel terminal sale.
const inFlightStartPayment = new Map<string, Promise<void>>()

const startPaymentFromAndroid = async (payload: StartPaymentRequest) => {
  const { orderId } = payload

  const inFlight = inFlightStartPayment.get(orderId)
  if (inFlight) {
    console.warn(`[useTerminal] startPayment ignored — already in flight for orderId=${orderId}`)
    return inFlight
  }

  // Money-safety gate: never open a terminal sale we cannot print a receipt for.
  // Runs right before the charge, so it covers BOTH the initial PAYMENT_METHOD flow
  // and the Confirmation retry path (Confirmation.tsx -> PAYMENT_PENDING). If the
  // printer is not ready, assertPrinterReady rejects and no charge is sent.
  const promise = assertPrinterReady()
    .then(() => invokeAndroidMethod<unknown>('startPayment', payload))
    .then(() => undefined)
    .finally(() => {
      inFlightStartPayment.delete(orderId)
    })

  inFlightStartPayment.set(orderId, promise)
  return promise
}

const cancelPaymentFromAndroid = async (payload: CancelPaymentRequest) => {
  await invokeAndroidMethod<unknown>('cancelPayment', payload)
}

const getPrinterStatusFromAndroid = async () => {
  const result = await invokeAndroidMethod<unknown>('getPrinterStatus')
  return parsePrinterStatus(result)
}

const printOrderFromAndroid = (payload: PrintOrderRequest) => invokeAndroidMethod<PrintOrderResult>('printOrder', payload)

const printPaymentConfirmationFromAndroid = (payload: PrintOrderRequest) =>
  invokeAndroidMethod<PrintOrderResult>('printPaymentConfirmation', payload)

const printTestReceiptFromAndroid = () => invokeAndroidMethod<PrintOrderResult>('printTestReceipt')

const useTerminal = ({ onPaymentStatusUpdate }: UseTerminalOptions = {}) => {
  useEffect(() => {
    if (!onPaymentStatusUpdate) {
      return
    }

    console.log('[useTerminal] registering window.onPaymentStatusUpdate handler')
    const previousHandler = window.onPaymentStatusUpdate
    const statusHandler = (statusPayload: unknown) => {
      console.log('[useTerminal] received payment status update', statusPayload)
      onPaymentStatusUpdate(parseStatusPayload(statusPayload))
    }

    window.onPaymentStatusUpdate = statusHandler

    return () => {
      if (window.onPaymentStatusUpdate === statusHandler) {
        console.log('[useTerminal] restoring previous window.onPaymentStatusUpdate handler')
        window.onPaymentStatusUpdate = previousHandler
      }
    }
  }, [onPaymentStatusUpdate])

  const startPayment = useCallback((payload: StartPaymentRequest) => startPaymentFromAndroid(payload), [])
  const cancelPayment = useCallback((payload: CancelPaymentRequest) => cancelPaymentFromAndroid(payload), [])
  const printOrder = useCallback((payload: PrintOrderRequest) => printOrderFromAndroid(payload), [])
  const printPaymentConfirmation = useCallback(
    (payload: PrintOrderRequest) => printPaymentConfirmationFromAndroid(payload),
    []
  )
  const printTestReceipt = useCallback(() => printTestReceiptFromAndroid(), [])
  const getPrinterStatus = useCallback(() => getPrinterStatusFromAndroid(), [])

  return {
    startPayment,
    cancelPayment,
    printOrder,
    printPaymentConfirmation,
    printTestReceipt,
    getPrinterStatus
  }
}

export default useTerminal
